import { FlexProps } from '@chakra-ui/react';
import { Amount, Flex, LinkButton, Paragraph, Radio } from '../../../atoms';
import { CardStatusTag } from '../components';
import {
  MoneyTemplateCard,
  MoneyTemplateStyleVariantType
} from '../money-template-card/money-template-card';

const getVariantStyle = ({
  isFulfilled,
  isSelected
}: {
  isFulfilled: boolean;
  isSelected: boolean;
}): MoneyTemplateStyleVariantType => {
  if (isFulfilled) {
    return 'fulfilled';
  }

  if (isSelected) {
    return 'selected';
  }

  return 'default';
};

type Props = {
  /**
   * To show if the budget has been actived.
   *
   * @default true
   */
  isActive?: boolean;
  /**
   * To show if the budget has been fulfilled.
   *
   * @default false
   */
  isFulfilled?: boolean;
  // TO BE IMPLIMENTED (https://www.figma.com/design/BET3dq3NMtLeEQHCaO8fo7/2.-Components?node-id=536-4194&t=9yMhhW1T9ea33bZ6-4)
  // /**
  //  * The card hinst on showing more details.
  //  *
  //  * @default false
  //  */
  // isClickable?: boolean;
  // /**
  //  * Handler when the card is clicked
  //  */
  // onClickDetail?: () => void;
  /**
   * Name of the budget
   */
  title: string;
  /**
   * To be shown below the title. e.g. expiration date
   */
  subtitle?: string;
  /**
   * Amount of the budget.
   */
  amount: number;
  // TO BE IMPLIMENTED (https://www.figma.com/design/BET3dq3NMtLeEQHCaO8fo7/2.-Components?node-id=536-4194&t=9yMhhW1T9ea33bZ6-4)
  // /**
  //  * Subtitle to be shown below the budget.
  //  * This can give extra info about the budget. e.g Holiday budget, consists out of 3 holidays.
  //  */
  // amountSubtitle?: string;
  /**
   * Budget cards can be selectable by a checkbox or radio button inside the component.
   */
  radioProps?: {
    isSelected: boolean;
    onClick: () => void;
    isDisabled?: boolean;
  };
  /**
   * Budget cards can have a action button. e.g Activate budget
   * Populate the props to show a action button
   */
  actionLink?: {
    hidden?: boolean;
    isExternal?: boolean;
    to: string;
    text: string;
    onClick?: () => void;
    /**
     * @default false
     */
    isDisabled?: boolean;
  };
} & FlexProps;

export function BudgetCard({
  radioProps,
  title,
  subtitle,
  amount,
  actionLink,
  isActive = true,
  isFulfilled = false,
  ...rest
}: Props): JSX.Element {
  const currentVariant: MoneyTemplateStyleVariantType = getVariantStyle({
    isFulfilled,
    isSelected: radioProps?.isSelected ?? false
  });

  const showActionLink = actionLink && !actionLink.hidden;

  const isSelectable = radioProps;

  return (
    <MoneyTemplateCard
      _hover={{
        cursor: isActive && radioProps ? 'pointer' : 'default'
      }}
      variant={currentVariant}
      {...rest}
      onClick={(): void => {
        if (isSelectable) {
          radioProps?.onClick();
        }
      }}
    >
      {!isActive && (
        <Flex padding={0} justifyContent="flex-end">
          <CardStatusTag
            variant="warning"
            positionedRelative
            marginBottom="10px"
          >
            Activation required
          </CardStatusTag>
        </Flex>
      )}
      <MoneyTemplateCard.Body
        {...(isActive && { paddingTop: '20px' })}
        {...(!showActionLink && { paddingBottom: '20px' })}
      >
        <Flex gap="space_unit.x1" flexDir="row" alignItems="center">
          {isSelectable && <Radio readOnly isChecked={radioProps.isSelected} />}
          <Flex flexDir="column">
            <Paragraph
              size="sm"
              fontWeight="semibold"
              textDecoration={isFulfilled ? 'line-through' : 'none'}
            >
              {title}
            </Paragraph>
            {subtitle && <Paragraph size="sm">{subtitle}</Paragraph>}
          </Flex>
        </Flex>
        <Flex>
          <Amount
            textDecoration={isFulfilled ? 'line-through' : 'none'}
            showPosSign={false}
            amount={amount}
            amountTextProps={{
              fontWeight: 'bold',
              color: 'black'
            }}
            currencyTextProps={{
              color: 'black'
            }}
          />
        </Flex>
      </MoneyTemplateCard.Body>
      {showActionLink && (
        <Flex paddingX="10px" paddingBottom="10px">
          <LinkButton
            variant="link"
            disabled={actionLink.isDisabled}
            onClick={(e) => {
              e.stopPropagation();
              actionLink?.onClick && actionLink.onClick();
            }}
            {...actionLink}
          >
            {actionLink.text}
          </LinkButton>
        </Flex>
      )}
    </MoneyTemplateCard>
  );
}
